<script lang="ts">
    import { scale, fade } from "svelte/transition";
    import { Button, DropdownShell, Dropdown, Slider } from "attractions";
    import Fa from "svelte-fa";
    import {
        faPlus,
        faPenToSquare,
        faWandMagicSparkles,
    } from "@fortawesome/free-solid-svg-icons";
    import AboutDialog from "./AboutDialog.svelte";
    import type { Puzzle, RandomParams } from "../types";
    import EditDialog from "./EditDialog.svelte";
    import { EMPTY_PUZZLE } from "../utils/puzzle";

    export let puzzle: Puzzle = null;
    export let onPuzzleChange: (puzzle: Puzzle) => void = null;
    export let onGenerateRandomPuzzle: (randomParams: RandomParams) => void =
        null;
    export let minimal: boolean = false;

    let currentDialog: string = "";
    const randomParams: RandomParams = { people: 5, features: 5 };

    const onClosePuzzleDialog = (puzzle: Puzzle) => {
        if (puzzle) {
            onPuzzleChange(puzzle);
        }
        currentDialog = null;
    };
</script>

<div class="root-container">
    <a class="logo-container" href="/">
        <img src="img/kraker.png" class="kraker-logo" />
        <span class="kraker-title">kraker</span>
    </a>
    <div class="actions-container">
        {#if !minimal}
            <Button
                class="action-btn"
                on:click={() => (currentDialog = "create")}
                >Create
                <div class="margin-left-sm">
                    <Fa icon={faPlus} />
                </div>
            </Button>
        {/if}

        {#if !minimal}
            <Button class="action-btn" on:click={() => (currentDialog = "edit")}
                >Edit
                <div class="margin-left-sm">
                    <Fa icon={faPenToSquare} />
                </div>
            </Button>
        {/if}

        {#if !minimal}
            <DropdownShell let:toggle>
                <Button on:click={toggle} class="action-btn">
                    Random
                    <div class="margin-left-sm">
                        <Fa icon={faWandMagicSparkles} />
                    </div>
                </Button>
                <Dropdown>
                    <div class="random-params-container">
                        <div class="random-param-container">
                            <div class="slider-container">
                                <Slider
                                    bind:value={randomParams.people}
                                    min={2}
                                    max={10}
                                    step={1}
                                    ticks={{
                                        mode: "step",
                                        step: 1,
                                        subDensity: 12,
                                    }}
                                />
                            </div>
                            <span>{randomParams.people} People</span>
                        </div>

                        <div class="random-param-container">
                            <div class="slider-container">
                                <Slider
                                    bind:value={randomParams.features}
                                    min={2}
                                    max={10}
                                    step={1}
                                    ticks={{
                                        mode: "step",
                                        step: 1,
                                        subDensity: 12,
                                    }}
                                />
                            </div>
                            <span>{randomParams.features} Features</span>
                        </div>

                        <Button
                            filled
                            class="action-btn"
                            on:click={() => {
                                toggle();
                                onGenerateRandomPuzzle(randomParams);
                            }}><span class="go-btn-text">Go!</span></Button
                        >
                    </div>
                </Dropdown>
            </DropdownShell>
        {/if}

        <Button class="action-btn" on:click={() => (currentDialog = "about")}
            >About</Button
        >
    </div>
    {#if currentDialog}
        <div transition:fade={{ duration: 200 }} class="dialog-overlay" />
    {/if}

    <div class="dialog-container">
        {#if currentDialog === "create"}
            <div transition:scale={{ duration: 200, start: 0.9 }}>
                <EditDialog
                    puzzle={EMPTY_PUZZLE}
                    onClose={onClosePuzzleDialog}
                />
            </div>
        {:else if currentDialog === "edit"}
            <div transition:scale={{ duration: 200, start: 0.9 }}>
                <EditDialog {puzzle} onClose={onClosePuzzleDialog} />
            </div>
        {:else if currentDialog === "about"}
            <div transition:scale={{ duration: 200, start: 0.9 }}>
                <AboutDialog onClose={() => (currentDialog = null)} />
            </div>
        {/if}
    </div>
</div>

<style lang="scss">
    @use "common.scss";
    @import "theme.scss";

    a {
        text-decoration: none;
        color: white;
    }

    .go-btn-text {
        width: 100%;
        text-align: center;
    }

    .slider-container {
        width: 6rem;
    }

    .random-params-container {
        width: 12rem;
        padding: 1.5rem;
        display: flex;
        flex-direction: column;
        align-items: stretch;
        justify-content: center;
        gap: 1rem;
    }

    .random-param-container {
        width: 100%;
        display: flex;
        justify-content: space-between;
        gap: 0.5rem;
        align-items: center;
    }

    .dialog-overlay {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        z-index: 150;
        opacity: 0.8;
        background: $background;
    }

    .dialog-container {
        position: absolute;
        left: 50%;
        transform: translate(-50%, 0);
        top: 5rem;
        z-index: 200;
    }

    :global(.dialog) {
        background: lighten($background, 10%) !important;
    }

    .root-container {
        width: 100%;
        height: 64px;
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        align-items: center;
        background: lighten($background, 5%);
    }

    .logo-container {
        margin-left: 1rem;
        display: flex;
        flex-direction: row;
        align-items: center;
        gap: 0.25rem;
    }

    .actions-container {
        margin-right: 1rem;
        display: flex;
        flex-direction: row;
        justify-content: end;
        align-items: center;
        gap: 0.5rem;
    }

    .actions-container :global(.action-btn) {
        margin-bottom: 0;
    }

    .kraker-logo {
        height: 40px;
    }

    .kraker-title {
        font-size: 28px;
        font-family: "Righteous", "Helvetica Neue", sans-serif;
    }
</style>
